import { useAutoFrameZoomView } from '@src/hooks/useAutoFrameZoomView';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { useReframeSpeakersTab } from '@src/hooks/useReframeSpeakersTab';
import { useUnmappedSpeakerApply } from '@src/hooks/useUnmappedSpeakerApply';
import { mapToElementState } from '@src/renderer/ElementState';
import SceneLayoutSelectionManager from '@src/stores/lib/SceneLayoutSelectionManager';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useReframeVideoModal,
  type UseReframeVideoModalDeps,
} from '../../stage/reframe-video-modal/useReframeVideoModal';

import type { AspectRatioOption } from '../../stage/reframe-video-modal/utils';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { Scene } from '@src/editor/models/layout/Scene';
import type { Renderer } from '@src/renderer/Renderer';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

/** Check if an element is a video or a composition containing a video child. */
function isVideoOrVideoComposition(element: Record<string, unknown>): boolean {
  const elType = element.type as string | undefined;
  if (elType === 'video') return true;
  if (elType === 'composition') {
    const children = element.elements as Array<{ type?: string }> | undefined;
    return !!children?.some(child => child.type === 'video');
  }
  return false;
}

export interface UseClipReframeModalParams {
  editorStore: ClipPreviewEditorStore;
  videoSourceStore: VideoSourceStore;
  renderer: Renderer | null;
  isReady: boolean;
  aspectRatio: string;
  /** Current playhead time from the adapter, used to initialize the modal at the correct frame */
  initialTime: number;
  closeModal: () => void;
  /** The Speakers tab is showing; its face data is fetched only then. */
  speakersTabActive?: boolean;
}

export function useClipReframeModal(params: UseClipReframeModalParams) {
  const {
    editorStore,
    videoSourceStore,
    renderer,
    isReady,
    aspectRatio,
    initialTime,
    closeModal,
    speakersTabActive = false,
  } = params;

  const videoCreator = useVideoCreatorStore();
  const [currentTime, setCurrentTime] = useState(initialTime);
  const timeRef = useRef(initialTime);

  // Use refs so callbacks always access the latest store instances
  const videoSourceStoreRef = useRef(videoSourceStore);
  videoSourceStoreRef.current = videoSourceStore;
  const editorStoreRef = useRef(editorStore);
  editorStoreRef.current = editorStore;

  // Attach time listener to renderer when it becomes available
  useEffect(() => {
    if (!renderer) return;
    const existingHandler = renderer.onTimeChange;
    renderer.onTimeChange = (time: number) => {
      existingHandler?.(time);
      timeRef.current = time;
      setCurrentTime(time);
    };
    return () => {
      // Restore the original handler on cleanup
      renderer.onTimeChange = existingHandler;
    };
  }, [renderer]);

  // Create SceneLayoutSelectionManager for the clip context
  const sceneLayoutSelectionManager = useMemo(() => {
    return new SceneLayoutSelectionManager(
      () => editorStoreRef.current.sceneLayoutIndexStore,
      (id: string) => {
        const el = videoSourceStoreRef.current.findElementById(id);
        return el ? mapToElementState(el) : undefined;
      },
      () => editorStoreRef.current.selectedElementIds,
      () => timeRef.current,
    );
  }, [editorStore, videoSourceStore]);

  const {
    showAutoFrameZoomView,
    autoFrameDisabledReason,
    handleAutoFrameZoomView,
    appliedVersion,
  } = useAutoFrameZoomView({
    editor: editorStore.editor,
    aspectRatio,
    storyId: videoCreator.story?.id,
    shots: videoCreator.sceneDetections,
    // Scene commands only mark the editor dirty; the clip preview needs an
    // explicit refresh to rebuild the layout index and repaint the renderer.
    onApplied: useCallback(() => {
      editorStoreRef.current.refreshTimeline(timeRef.current);
    }, []),
  });

  /**
   * Bumped after a manual apply so the `scenes` memo re-reads the timeline.
   * `commandUpdateScene` does not mutate the scene in place — it builds a
   * replacement via `Scene.fromJSON` and swaps the whole array through
   * `timeline.setScenes`. The memoized array therefore still holds the
   * pre-apply `Scene`, and its pre-apply `layout.bindings` are exactly where
   * `setDefaults` reads each slot's crop from: the applied framing reached the
   * timeline and the renderer, but coming back to the scene re-derived the
   * original crop, so Apply looked like it had never saved.
   */
  const [appliedCropVersion, setAppliedCropVersion] = useState(0);

  /** Bumped after a Speakers-tab assignment reframed scenes; same reason. */
  const [speakersAppliedVersion, setSpeakersAppliedVersion] = useState(0);

  const scenes = useMemo<readonly Scene[]>(() => {
    return editorStore.editor.timeline.scenes;
  }, [
    editorStore,
    editorStore.isReady,
    appliedVersion,
    appliedCropVersion,
    speakersAppliedVersion,
  ]);

  /**
   * Bumped by `goToScene` so the slots re-derive for the scene navigated to.
   * A counter rather than an effect on `currentTime`, which the renderer's own
   * `onTimeChange` also drives — resetting slot state on a playback tick would
   * throw away crop edits nobody asked to discard.
   */
  const [sceneNavVersion, setSceneNavVersion] = useState(0);

  /**
   * Move the reframe modal to a moment in the clip — the scenes strip's
   * click-to-seek. Sets the time optimistically as well as seeking the
   * renderer behind the modal, so the slots re-resolve even if the renderer's
   * `onTimeChange` echo is slow or severed.
   *
   * `ensureWindowCovers` first, and awaited: the preview source holds only the
   * windowed slice of the timeline, so jumping outside it and re-deriving
   * straight away finds no element at the playhead and the modal falls to its
   * "No video in this section" state. Same guard `ClipVideoCreatorAdapter`
   * and `useClipPreviewRenderer` put in front of their own seeks; a raw
   * `renderer.setTime` skips it.
   */
  const goToScene = useCallback(
    async (time: number) => {
      timeRef.current = time;
      setCurrentTime(time);
      await editorStoreRef.current.ensureWindowCovers(time);
      await renderer?.setTime(time);
      // Bumped last, so the slots re-derive against a source that covers the
      // destination rather than the window we just left.
      setSceneNavVersion(version => version + 1);
    },
    [renderer],
  );

  // Find the original video element at the current playhead time.
  // Reads from the videoSourceStore via ref for latest MobX state.
  const findOriginalVideoElementAtCurrentTime = useCallback((): Record<
    string,
    unknown
  > | null => {
    const store = videoSourceStoreRef.current;
    const elements = store?.source?.elements as
      | Array<Record<string, unknown>>
      | undefined;
    if (!elements) return null;

    for (const element of elements) {
      if (!isVideoOrVideoComposition(element)) {
        continue;
      }
      const elTime = parseFloat(String(element.time ?? 0));
      if (timeRef.current < elTime) {
        return null;
      }
      const duration = parseFloat(String(element.duration));
      if (timeRef.current < elTime + duration) {
        return element;
      }
    }
    return null;
  }, []);

  const isOriginalVideoElement = useCallback(
    (element: Record<string, unknown>): boolean =>
      isVideoOrVideoComposition(element),
    [],
  );

  const findElementById = useCallback(
    (id: string): Record<string, unknown> | undefined => {
      return videoSourceStoreRef.current.findElementById(id);
    },
    [],
  );

  const setActiveElements = useCallback(
    async (elementId: string): Promise<void> => {
      editorStoreRef.current.selectElement(elementId);
    },
    [],
  );

  const unmappedSpeaker = useUnmappedSpeakerApply(
    editorStore.editor,
    aspectRatio,
  );

  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const editorAudioUrl = videoCreator.story?.transcription?.audio?.url;

  // Build deps for the shared useReframeVideoModal hook
  const deps = useMemo<UseReframeVideoModalDeps>(
    () => ({
      time: currentTime,
      defaultVideoAspectRatio:
        (videoCreator.getDefaultAspectRatio() as AspectRatioOption) || '16:9',
      currentVideoAspectRatio: (aspectRatio as AspectRatioOption) || '16:9',
      hasCurrentVideo: isReady && !!videoSourceStore?.source,
      scenes,
      sceneLayoutIndexStore: editorStore.sceneLayoutIndexStore,
      sceneLayoutSelectionManager: sceneLayoutSelectionManager!,
      rendererSyncService: editorStore.rendererSyncService ?? null,
      onAfterApply: () => {
        // Push scene changes to the renderer by refreshing the timeline.
        // The RendererSyncService updates scenes via acceptExternalChanges
        // which suppresses the editor observer's auto-refresh.
        editorStoreRef.current.refreshTimeline();
        // The applied crop lives on a replacement Scene instance, so re-read
        // the timeline's scenes — see `appliedCropVersion`.
        setAppliedCropVersion(version => version + 1);
      },
      findElementById,
      isOriginalVideoElement,
      findOriginalVideoElementAtCurrentTime,
      setActiveElements,
      closeModal,
      unmappedSpeaker,
      localPreviewUrl,
      editorAudioUrl,
    }),
    [
      currentTime,
      videoCreator,
      aspectRatio,
      isReady,
      videoSourceStore,
      scenes,
      editorStore,
      sceneLayoutSelectionManager,
      findElementById,
      isOriginalVideoElement,
      findOriginalVideoElementAtCurrentTime,
      setActiveElements,
      closeModal,
      unmappedSpeaker,
      localPreviewUrl,
      editorAudioUrl,
    ],
  );

  const modal = useReframeVideoModal(deps);

  const speakersTab = useReframeSpeakersTab({
    active: speakersTabActive,
    editor: editorStore.editor,
    aspectRatio,
    sceneId: modal.orderedSlots[0]?.sceneId,
    rendererSyncService: editorStore.rendererSyncService ?? null,
    onAfterApply: useCallback(() => {
      editorStoreRef.current.refreshTimeline(timeRef.current);
      setSpeakersAppliedVersion(version => version + 1);
    }, []),
  });

  /**
   * Read by the scene-navigation effect below, which must not re-run when the
   * creator ticks the box — only when they actually move to another scene.
   */
  const applyToAllScenesRef = useRef(modal.applyToAllScenes);
  applyToAllScenesRef.current = modal.applyToAllScenes;

  // Re-derive the slots from the rewritten scenes. Deferred to an effect
  // because `setDefaults` reads through `scenes`, which only updates on the
  // render that follows the version bump.
  const setDefaultsRef = useRef(modal.setDefaults);
  setDefaultsRef.current = modal.setDefaults;

  useEffect(() => {
    if (appliedVersion === 0) return;
    setDefaultsRef.current();
  }, [appliedVersion]);

  useEffect(() => {
    if (speakersAppliedVersion === 0) return;
    setDefaultsRef.current();
  }, [speakersAppliedVersion]);

  // Same deferral for scene navigation: `setDefaults` resolves the slots
  // through `deps.time`, which only carries the new time from the render that
  // follows the state update above.
  //
  // `keepCrops` while "Apply framing to all scenes" is ticked: the scene strip
  // then previews rather than navigates — the picture moves to the scene the
  // creator clicked, still wearing the crop every scene is about to be given.
  useEffect(() => {
    if (sceneNavVersion === 0) return;
    setDefaultsRef.current({ keepCrops: applyToAllScenesRef.current });
  }, [sceneNavVersion]);

  return {
    ...modal,
    showAutoFrameZoomView,
    autoFrameDisabledReason,
    handleAutoFrameZoomView,
    scenes,
    sceneElements: editorStore.editor.timeline.allElements,
    clipDuration: editorStore.editor.timeline.duration,
    currentTime,
    goToScene,
    speakersTab,
    unmappedSpeaker,
  };
}
